import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Checkbox,
  FormControlLabel,
  Box,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  SelectChangeEvent,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { LoaderSpinner2 } from "../../../../components";
import { Ruleset } from "store/reducers/products/parameters/pl-parameters.types";
import { PropertyResponse } from "store/reducers/products/properties/pl-properties.types";
import { EdgeDetailsPopup } from "./EdgeDetailsPopup.component";
import {
  useDialogSize,
  useGraphData,
  useGraphvizDot,
  useGraphEdgeInteraction,
} from "./GraphvizHooks";
import styled from "styled-components";
import SafeGraphviz from "./SafeGraphviz";

interface GraphvizPopUpProps {
  open: boolean;
  onClose: () => void;
  productLineId: string;
  propertyData: PropertyResponse;
  ruleSets: Ruleset[];
}

// Size configurations for different dialog sizes
const sizeConfigs = {
  md: {
    graphHeight: 400,
    graphWidth: 850,
    minHeight: "400px",
  },
  lg: {
    graphHeight: 550,
    graphWidth: 1100,
    minHeight: "550px",
  },
  xl: {
    graphHeight: 700,
    graphWidth: 1400,
    minHeight: "700px",
  },
};

interface GraphToolbarProps {
  subpropertyFilter: boolean;
  onSubpropertyFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedRulesets: string[];
  onRulesetChange: (event: SelectChangeEvent<string[]>) => void;
  ruleSets: Ruleset[];
  pvaFilter: boolean;
  onPvaFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const GraphToolbar: React.FC<GraphToolbarProps> = ({
  subpropertyFilter,
  onSubpropertyFilterChange,
  selectedRulesets,
  onRulesetChange,
  ruleSets,
  pvaFilter,
  onPvaFilterChange,
}) => (
  <Grid container spacing={2} alignItems="center">
    <Grid item xs={6}>
      <FormControlLabel
        control={
          <Checkbox
            checked={subpropertyFilter}
            onChange={onSubpropertyFilterChange}
          />
        }
        label="Include Subproperties"
      />
      <FormControlLabel
        control={<Checkbox checked={pvaFilter} onChange={onPvaFilterChange} />}
        label="Include Property Value Attributes"
      />
    </Grid>
    <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Select
        sx={{ width: "250px" }}
        value={selectedRulesets}
        onChange={onRulesetChange}
        multiple
        displayEmpty
      >
        <MenuItem value="all">
          {selectedRulesets.length === ruleSets.length
            ? "Deselect All"
            : "Select All"}
        </MenuItem>
        {ruleSets.map((ruleset) => (
          <MenuItem key={ruleset.id} value={ruleset.id}>
            {ruleset.name}
          </MenuItem>
        ))}
      </Select>
    </Grid>
  </Grid>
);

const GraphvizPopUp: React.FC<GraphvizPopUpProps> = ({
  open,
  onClose,
  productLineId,
  propertyData,
  ruleSets,
}) => {
  const [subpropertyFilter, setSubpropertyFilter] = useState<boolean>(true);
  const [showEdgePopup, setShowEdgePopup] = useState<boolean>(false);
  const [popUpTables, setPopUpTable] = useState<string[]>([]);
  const [pvaFilter, setPvaFilter] = useState<boolean>(true);
  const graphvizRef = useRef<HTMLDivElement>(null);

  const { dialogSize, handleSizeToggle } = useDialogSize("lg");
  const {
    isLoading,
    setIsLoading,
    filteredTables,
    selectedRulesets,
    setSelectedRulesets,
  } = useGraphData(open, productLineId, propertyData.id || "", ruleSets);

  const dotContent = useGraphvizDot(
    filteredTables,
    propertyData.name,
    subpropertyFilter,
    pvaFilter,
    setIsLoading
  );

  const handleRulesetChange = (
    event: SelectChangeEvent<typeof selectedRulesets>
  ) => {
    const { value } = event.target;
    const newValues = typeof value === "string" ? value.split(",") : value;

    if (newValues.includes("all")) {
      setSelectedRulesets(
        selectedRulesets.length === ruleSets.length
          ? []
          : ruleSets.map((rs) => rs.id)
      );
    } else {
      setSelectedRulesets(newValues);
    }
  };

  const handleSubpropertyFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSubpropertyFilter(e.target.checked);
  };

  const handlePvaFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPvaFilter(e.target.checked);
  };

  /**
   * Handles click events on graph edges.
   * Parses the edge ID to extract property name, table type, and direction.
   * Filters tables based on these parameters and updates state to show relevant tables in a popup.
   *
   * @param edgeId - String identifier of the clicked edge in format "property,tableType,direction"
   */
  const handleEdgeClick = useCallback(
    (edgeId: string) => {
      const [property, tableType, direction] = edgeId.split(",");
      const popUpTables = filteredTables.filter((table) => {
        return (
          table.properties.map((prop: any) => prop.name).includes(property) &&
          table.tableType === tableType &&
          ((direction === "forward" &&
            table.properties
              .slice(table.lookUpFieldIndex)
              .map((prop: any) => prop.name)
              .includes(property)) ||
            (direction === "back" &&
              table.properties
                .slice(0, table.lookUpFieldIndex)
                .map((prop: any) => prop.name)
                .includes(property)) ||
            direction === "both")
        );
      });

      setPopUpTable(popUpTables.map((table) => table.id));
      setShowEdgePopup(true);
    },
    [filteredTables]
  );

  useGraphEdgeInteraction(
    graphvizRef,
    isLoading,
    dotContent,
    handleEdgeClick,
    showEdgePopup
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={dialogSize}
        fullWidth={true}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {propertyData.name} related properties
          <Box>
            <Tooltip
              title={
                dialogSize === "md"
                  ? "Medium Size"
                  : dialogSize === "lg"
                  ? "Large Size"
                  : "Extra Large Size"
              }
            >
              <IconButton onClick={handleSizeToggle}>
                {dialogSize === "md" && <FullscreenIcon />}
                {dialogSize === "lg" && <AspectRatioIcon />}
                {dialogSize === "xl" && <FullscreenExitIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <GraphToolbar
                subpropertyFilter={subpropertyFilter}
                onSubpropertyFilterChange={handleSubpropertyFilterChange}
                selectedRulesets={selectedRulesets}
                onRulesetChange={handleRulesetChange}
                ruleSets={ruleSets}
                pvaFilter={pvaFilter}
                onPvaFilterChange={handlePvaFilterChange}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                minHeight: sizeConfigs[dialogSize].minHeight,
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <LoaderSpinner2 />
                </div>
              ) : (
                open && (
                  <GraphWrapper ref={graphvizRef}>
                    <SafeGraphviz
                      dot={dotContent}
                      options={{
                        fit: true,
                        height: sizeConfigs[dialogSize].graphHeight,
                        width: sizeConfigs[dialogSize].graphWidth,
                        zoom: true,
                        engine: "neato",
                      }}
                    />
                  </GraphWrapper>
                )
              )}
            </Grid>
            <Grid item xs={12} display={"flex"}>
              <Typography variant="h6">Legend: </Typography>
              <img src="/LegendGraph.png" alt="graph" />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      {showEdgePopup && (
        <EdgeDetailsPopup
          tablesId={popUpTables}
          showEdgePopup={showEdgePopup}
          setShowEdgePopup={setShowEdgePopup}
          productLineId={productLineId}
        />
      )}
    </>
  );
};

const GraphWrapper = styled.div`
  justify-self: center;
`;

export { GraphvizPopUp };

export default GraphvizPopUp;
